import { useMemo, useState } from "react";
import type { TFunction } from "i18next";
import { createColumnHelper, type SortingState } from "@tanstack/react-table";
import { Plus } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import { exportHref } from "@jbnl/api-client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DataTable,
  DataTableSkeleton,
  dataTableFeatures,
} from "@/components/ui/data-table";
import type {
  Filterdescriptor,
  Filterstand,
} from "@/components/ui/data-table-filter";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Pager } from "@/components/ui/pager";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { FormActions, Toolbar } from "@jbnl/shared-ui";
import { ListDetail } from "@/components/ListDetail";
import { FULL_PAGE_SIZE } from "@/lib/paging";
import {
  useWedstrijddagen,
  type Seizoen,
  type Wedstrijddag,
} from "@/features/wedstrijden/useWedstrijden";
import { Aankondiging } from "./Aankondiging";
import { InschrijfperiodeForm } from "./InschrijfperiodeForm";
import { VensterBadge } from "./VensterBadge";
import { vensterStatus } from "./venster";
import {
  DOELGROEP,
  doelgroepLabel,
  useCreateInschrijfperiode,
  useDeleteInschrijfperiode,
  useInschrijfperiodes,
  useUpdateInschrijfperiode,
  type Doelgroep,
  type Inschrijfperiode,
} from "./useInschrijfperiodes";

const helper = createColumnHelper<typeof dataTableFeatures, Inschrijfperiode>();

/** Only the one: "status" is `isOpen` against the clock, which no column can order by. */
const SORT_BY: Record<string, string> = { periode: "name" };

/**
 * Two columns, not four: four pushed the list pane into a horizontal scroll, putting the status
 * badge out of sight. The accessor still returns both words, so the search box keeps its promise.
 */
const kolommen = (t: TFunction<"inschrijfperiodes">) =>
  helper.columns([
    helper.accessor(
      (periode) => `${periode.name} ${doelgroepLabel(periode.targetGroup)}`,
      {
        id: "periode",
        header: t("lijst.periode"),
        cell: (info) => (
          <span>
            <span className="font-medium">{info.row.original.name}</span>
            <span className="block text-xs text-muted-foreground">
              {doelgroepLabel(info.row.original.targetGroup)}
            </span>
          </span>
        ),
      },
    ),
    helper.accessor(
      (periode) =>
        periode.isOpen ? t("venster.badgeOpen") : t("venster.badgeGesloten"),
      {
        id: "status",
        header: t("lijst.status"),
        // Rendered as the badge but *sorted and searched* as the word: a beheerder looking for
        // the one window still open types "open", and a column of coloured dots answers nothing.
        cell: (info) => <VensterBadge open={info.row.original.isOpen} />,
      },
    ),
  ]);

/**
 * §3.1. Both doelgroepen in one list rather than two tabs, because periodes may overlap and that
 * is a thing you have to see happening. The seizoen is fixed by the URL.
 */
export function Inschrijfperiodes({
  seizoen,
  periodeId,
  onKies,
  pagina,
  onPaginaChange,
  doelgroep,
  status,
  onFiltersChange,
}: {
  seizoen: Seizoen;
  periodeId: string | undefined;
  onKies: (id?: string) => void;
  pagina: number | undefined;
  onPaginaChange: (pagina: number) => void;
  /** The list's own filters (#193), read from the URL by the page. */
  doelgroep: string | undefined;
  status: string | undefined;
  onFiltersChange: (volgende: {
    doelgroep: string | undefined;
    status: string | undefined;
  }) => void;
}) {
  const { t } = useTranslation("inschrijfperiodes");
  const { data: dagen } = useWedstrijddagen(seizoen.id);

  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState<SortingState>([]);
  const sort = sorting[0];

  // Server-driven, so neither descriptor carries `past` and the facets show no counts: this
  // list is one page of an answer the server already narrowed (#193).
  const descriptors = useMemo<Filterdescriptor<Inschrijfperiode>[]>(
    () => [
      {
        id: "doelgroep",
        label: t("lijst.filterDoelgroep"),
        kind: "keuze",
        opties: [
          {
            waarde: String(DOELGROEP.korpsen),
            label: doelgroepLabel(DOELGROEP.korpsen),
          },
          {
            waarde: String(DOELGROEP.ondersteuners),
            label: doelgroepLabel(DOELGROEP.ondersteuners),
          },
        ],
      },
      {
        id: "status",
        label: t("lijst.filterStatus"),
        kind: "keuze",
        opties: [
          { waarde: "true", label: t("venster.badgeOpen") },
          { waarde: "false", label: t("venster.badgeGesloten") },
        ],
      },
    ],
    [t],
  );
  const stand: Filterstand = {
    ...(doelgroep && { doelgroep: { soort: "keuze", waarde: doelgroep } }),
    ...(status && { status: { soort: "keuze", waarde: status } }),
  };
  const targetGroup = doelgroep ? (Number(doelgroep) as Doelgroep) : undefined;
  const isOpen = status === undefined ? undefined : status === "true";

  const { data: page, isPending } = useInschrijfperiodes(
    seizoen.id,
    pagina,
    FULL_PAGE_SIZE,
    search,
    sort && SORT_BY[sort.id],
    sort?.desc ?? false,
    targetGroup,
    isOpen,
  );
  const periode = page?.items.find((p) => p.id === periodeId);

  return (
    <>
      <Toolbar>
        <NieuweInschrijfperiode
          seizoen={seizoen}
          dagen={dagen ?? []}
          onAangemaakt={onKies}
        />
      </Toolbar>

      <ListDetail
        selected={Boolean(periode)}
        backLabel={t("lijst.terug")}
        onBack={() => onKies()}
        list={
          <InschrijfperiodeLijst
            exportLink={exportHref(
              "/api/beheer/seizoenen/{seizoenId}/inschrijfperiodes/export",
              {
                path: { seizoenId: seizoen.id },
                query: {
                  search,
                  sortBy: sort && SORT_BY[sort.id],
                  sortDescending: sort?.desc ?? false,
                  targetGroup,
                  isOpen,
                },
              },
            )}
            page={page}
            isPending={isPending}
            selectedId={periode?.id}
            onSelect={onKies}
            search={search}
            onSearchChange={setSearch}
            sorting={sorting}
            onSortingChange={setSorting}
            onPaginaChange={onPaginaChange}
            descriptors={descriptors}
            stand={stand}
            onStand={(nieuw) => {
              const volgende = (id: string) => {
                const waarde = nieuw[id];
                return waarde?.soort === "keuze"
                  ? waarde.waarde || undefined
                  : undefined;
              };
              onFiltersChange({
                doelgroep: volgende("doelgroep"),
                status: volgende("status"),
              });
            }}
          />
        }
        detail={
          periode ? (
            // Keyed on the id: the form inside is state seeded from the record, so choosing
            // another periode resets it by remounting rather than by an effect that would also
            // fire on every background refetch.
            <InschrijfperiodeDetail
              key={periode.id}
              seizoen={seizoen}
              dagen={dagen ?? []}
              periode={periode}
              onVerwijderd={() => onKies()}
            />
          ) : (
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">
                  {t("lijst.kies")}
                </p>
              </CardContent>
            </Card>
          )
        }
      />
    </>
  );
}

function InschrijfperiodeLijst({
  page,
  isPending,
  selectedId,
  onSelect,
  search,
  onSearchChange,
  sorting,
  onSortingChange,
  onPaginaChange,
  descriptors,
  stand,
  onStand,
  exportLink,
}: {
  page: ReturnType<typeof useInschrijfperiodes>["data"];
  isPending: boolean;
  selectedId: string | undefined;
  onSelect: (id?: string) => void;
  search: string;
  onSearchChange: (search: string) => void;
  sorting: SortingState;
  onSortingChange: (sorting: SortingState) => void;
  onPaginaChange: (pagina: number) => void;
  descriptors: Filterdescriptor<Inschrijfperiode>[];
  stand: Filterstand;
  onStand: (stand: Filterstand) => void;
  exportLink: string;
}) {
  const { t } = useTranslation("inschrijfperiodes");
  const columns = kolommen(t);

  return (
    <Card>
      {/* No CardHeader: the page title above both panes already says the noun. */}
      <CardContent className="space-y-4 pt-6">
        {isPending || !page ? (
          <DataTableSkeleton
            label={t("lijst.label")}
            columns={columns}
            rows={3}
            search
            twoLine={["periode"]}
          />
        ) : (
          <>
            <DataTable
              label={t("lijst.label")}
              data={page.items}
              columns={columns}
              getRowId={(row) => row.id}
              selectedId={selectedId}
              onSelect={onSelect}
              searchPlaceholder={t("lijst.zoek")}
              empty={t("lijst.leeg")}
              exportHref={exportLink}
              serverDriven={{
                sorting,
                onSortingChange,
                globalFilter: search,
                // A search narrows the set the server counts, so page 2 of the old set
                // would otherwise land past the end of the new, shorter one.
                onGlobalFilterChange: (value) => {
                  onSearchChange(value);
                  onPaginaChange(1);
                },
              }}
              filters={{
                descriptors,
                stand,
                onStand,
                toonTelling: false,
              }}
            />
            <Pager
              page={page.page}
              pageSize={page.pageSize}
              totalCount={page.totalCount}
              onPageChange={onPaginaChange}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}

function NieuweInschrijfperiode({
  seizoen,
  dagen,
  onAangemaakt,
}: {
  seizoen: Seizoen;
  dagen: Wedstrijddag[];
  onAangemaakt: (id: string) => void;
}) {
  const { t } = useTranslation("inschrijfperiodes");
  const create = useCreateInschrijfperiode(seizoen.id);
  const [open, setOpen] = useState(false);

  function wissel(volgende: boolean) {
    setOpen(volgende);
    if (!volgende) create.reset();
  }

  return (
    <Dialog open={open} onOpenChange={wissel}>
      <DialogTrigger asChild>
        <Button>
          <Plus />
          {t("lijst.toevoegen")}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("lijst.toevoegen")}</DialogTitle>
          <DialogDescription>
            {t("lijst.aanSeizoen", { naam: seizoen.name })}
          </DialogDescription>
        </DialogHeader>

        {/* Keyed on whether the modal is open, so every visit starts on an empty form rather
            than on what was abandoned last time. */}
        <InschrijfperiodeForm
          key={String(open)}
          id="nieuwe-periode"
          dagen={dagen}
          submitLabel={t("lijst.submitToevoegen")}
          pending={create.isPending}
          error={create.error}
          onSubmit={(velden) =>
            create.mutate(velden, {
              onSuccess: (nieuw) => {
                wissel(false);
                onAangemaakt(nieuw.id);
              },
            })
          }
        />
      </DialogContent>
    </Dialog>
  );
}

function InschrijfperiodeDetail({
  seizoen,
  dagen,
  periode,
  onVerwijderd,
}: {
  seizoen: Seizoen;
  dagen: Wedstrijddag[];
  periode: Inschrijfperiode;
  onVerwijderd: () => void;
}) {
  const { t } = useTranslation(["inschrijfperiodes", "common"]);
  const update = useUpdateInschrijfperiode(seizoen.id);
  const remove = useDeleteInschrijfperiode(seizoen.id);
  const status = vensterStatus(periode);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex flex-wrap items-center gap-3">
            {periode.name}
            <VensterBadge open={periode.isOpen} />
            {periode.announcedAt && (
              <span className="inline-flex shrink-0 items-center rounded-full bg-muted px-2 py-0.5 text-xs font-bold text-muted-foreground">
                {t("detail.aangekondigd")}
              </span>
            )}
          </CardTitle>
          <p className="text-sm text-muted-foreground">{status.label}</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <InschrijfperiodeForm
            id="periode"
            periode={periode}
            dagen={dagen}
            submitLabel={t("common:actions.save")}
            pending={update.isPending}
            error={update.error}
            onSubmit={(velden) => update.mutate({ ...velden, id: periode.id })}
          />
          {/* Reopening is moving "Sluit op" forwards, which the audit allowlist records whole. */}
          <p className="text-xs text-muted-foreground">
            <Trans
              t={t}
              i18nKey="detail.heropenen"
              components={{ strong: <strong /> }}
            />
          </p>
        </CardContent>
      </Card>

      <Aankondiging seizoen={seizoen} periode={periode} />

      <Card>
        <CardHeader>
          <CardTitle>{t("detail.verwijderenTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {t("detail.verwijderenUitleg")}
          </p>
          <FormActions>
            {remove.error && (
              <p className="text-sm text-destructive">{remove.error.message}</p>
            )}
            <ConfirmDelete
              trigger={
                <Button variant="destructive" disabled={remove.isPending}>
                  {remove.isPending
                    ? t("common:actions.busy")
                    : t("common:actions.delete")}
                </Button>
              }
              title={t("detail.verwijderenVraag", { naam: periode.name })}
              description={t("detail.verwijderenDescription")}
              confirmLabel={t("detail.verwijderenConfirm")}
              onConfirm={() =>
                remove.mutate(periode.id, { onSuccess: onVerwijderd })
              }
            />
          </FormActions>
        </CardContent>
      </Card>
    </div>
  );
}
